import { LucideIcon } from 'lucide-react';
import React from 'react';

// ===== BRAND GUIDELINE COMPONENTS =====

// PageHeader Component - Following Brand Guidelines
interface PageHeaderProps {
  title: string;
  description?: string;
  breadcrumbs?: Array<{ label: string; href?: string }>;
  primaryAction?: {
    label: string;
    onClick: () => void;
    icon?: React.ReactNode;
  };
  secondaryActions?: Array<{
    label: string;
    onClick: () => void;
    icon?: React.ReactNode;
  }>;
  contextChip?: React.ReactNode;
}

export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  description,
  breadcrumbs,
  primaryAction,
  secondaryActions,
  contextChip,
}) => {
  return (
    <div className="mb-6">
      {breadcrumbs && breadcrumbs.length > 0 && (
        <nav className="flex mb-2" aria-label="Breadcrumb">
          <ol className="flex items-center space-x-2">
            {breadcrumbs.map((crumb, index) => (
              <li key={index} className="flex items-center">
                {index > 0 && <span className="mx-2 text-gray-400">/</span>}
                {crumb.href ? (
                  <a href={crumb.href} className="text-body-sm text-gray-600 hover:text-gray-900">
                    {crumb.label}
                  </a>
                ) : (
                  <span className="text-body-sm text-gray-900">{crumb.label}</span>
                )}
              </li>
            ))}
          </ol>
        </nav>
      )}
      <div className="flex items-start justify-between">
        <div className="flex items-center space-x-3">
          <div>
            <h1 className="text-heading-lg text-gray-900">{title}</h1>
            {description && <p className="text-body-md text-gray-600 mt-1">{description}</p>}
          </div>
          {contextChip}
        </div>
        {(primaryAction || secondaryActions) && (
          <div className="flex items-center space-x-3">
            {secondaryActions?.map((action, index) => (
              <button key={index} onClick={action.onClick} className="btn btn-secondary">
                {action.icon}
                {action.label}
              </button>
            ))}
            {primaryAction && (
              <button onClick={primaryAction.onClick} className="btn btn-primary">
                {primaryAction.icon}
                {primaryAction.label}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// SummaryStatRow Component - Compact KPI Row
interface StatItem {
  icon: LucideIcon;
  label: string;
  value: string | number;
  change?: {
    value: number;
    trend: 'up' | 'down';
  };
}

interface SummaryStatRowProps {
  stats: StatItem[];
}

export const SummaryStatRow: React.FC<SummaryStatRowProps> = ({ stats }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 section-gap">
      {stats.map((stat, index) => {
        const Icon = stat.icon;
        return (
          <div key={index} className="card-base">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-50 rounded-md">
                <Icon className="icon-md text-gray-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-meta text-gray-500 truncate">{stat.label}</p>
                <p className="text-heading-sm text-gray-900 font-semibold">{stat.value}</p>
                {stat.change && (
                  <p
                    className={`text-meta font-medium ${
                      stat.change.trend === 'up' ? 'text-green-600' : 'text-red-600'
                    }`}
                  >
                    {stat.change.trend === 'up' ? '↗' : '↘'} {Math.abs(stat.change.value)}%
                  </p>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// DataTable Component - Following Brand Guidelines
interface Column<T> {
  key: string;
  header: string;
  render?: (item: T) => React.ReactNode;
  width?: string;
}

interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  onRowClick?: (item: T) => void;
  rowHeight?: 'default' | 'compact';
  stickyHeader?: boolean;
  actions?: (item: T) => React.ReactNode;
}

export function DataTable<T extends { id?: string; _id?: string }>({
  data,
  columns,
  onRowClick,
  rowHeight = 'default',
  stickyHeader = true,
  actions,
}: DataTableProps<T>) {
  const tableClass = 'table-base';
  const headerClass = stickyHeader ? 'table-header' : 'bg-gray-50 border-b border-gray-200';
  const cellClass = rowHeight === 'compact' ? 'table-cell' : 'table-cell-lg';

  return (
    <div className="overflow-x-auto">
      <table className={tableClass}>
        <thead className={headerClass}>
          <tr>
            {columns.map(column => (
              <th
                key={column.key}
                className="table-cell text-left text-meta font-medium text-gray-500 uppercase tracking-wider"
                style={{ width: column.width }}
              >
                {column.header}
              </th>
            ))}
            {actions && (
              <th className="table-cell text-right text-meta font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {data.map(item => {
            const key = item.id || item._id || JSON.stringify(item);
            return (
              <tr
                key={key}
                className={`table-row ${onRowClick ? 'cursor-pointer' : ''}`}
                onClick={() => onRowClick?.(item)}
              >
                {columns.map(column => (
                  <td key={column.key} className={cellClass}>
                    {column.render ? column.render(item) : (item as any)[column.key] || '-'}
                  </td>
                ))}
                {actions && <td className={`${cellClass} table-actions`}>{actions(item)}</td>}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// EmptyState Component
interface EmptyStateProps {
  icon: LucideIcon;
  title: string;
  description: string;
  action?: {
    label: string;
    onClick: () => void;
  };
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  icon: Icon,
  title,
  description,
  action,
}) => {
  return (
    <div className="text-center py-16">
      <div className="w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl flex items-center justify-center mx-auto mb-6">
        <Icon className="h-10 w-10 text-gray-400" />
      </div>
      <h3 className="text-heading-md text-gray-900 mb-3">{title}</h3>
      <p className="text-body-md text-gray-600 mb-8 max-w-md mx-auto">{description}</p>
      {action && (
        <button onClick={action.onClick} className="btn btn-primary">
          {action.label}
        </button>
      )}
    </div>
  );
};

// KeyValueList Component
interface KeyValueItem {
  key: string;
  value: React.ReactNode;
}

interface KeyValueListProps {
  items: KeyValueItem[];
  columns?: 1 | 2 | 3;
}

export const KeyValueList: React.FC<KeyValueListProps> = ({ items, columns = 1 }) => {
  const getGridCols = () => {
    switch (columns) {
      case 2:
        return 'grid-cols-1 md:grid-cols-2';
      case 3:
        return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3';
      default:
        return 'grid-cols-1';
    }
  };

  return (
    <dl className={`grid ${getGridCols()} gap-4`}>
      {items.map((item, index) => (
        <div key={index}>
          <dt className="text-body-sm text-gray-500">{item.key}</dt>
          <dd className="text-body-md text-gray-900 mt-1">{item.value}</dd>
        </div>
      ))}
    </dl>
  );
};

// Section Component
interface SectionProps {
  title?: string;
  description?: string;
  children: React.ReactNode;
  actions?: React.ReactNode;
}

export const Section: React.FC<SectionProps> = ({ title, description, children, actions }) => {
  return (
    <div className="mb-6">
      {(title || actions) && (
        <div className="flex items-center justify-between mb-4">
          <div>
            {title && <h2 className="text-heading-md text-gray-900">{title}</h2>}
            {description && <p className="text-body-sm text-gray-600 mt-1">{description}</p>}
          </div>
          {actions}
        </div>
      )}
      {children}
    </div>
  );
};

// AlertBanner Component
interface AlertBannerProps {
  variant: 'info' | 'success' | 'warning' | 'error';
  message: string;
  onClose?: () => void;
}

export const AlertBanner: React.FC<AlertBannerProps> = ({ variant, message, onClose }) => {
  const getVariantClasses = () => {
    switch (variant) {
      case 'success':
        return 'bg-status-success-50 border-status-success-100 text-status-success-700';
      case 'warning':
        return 'bg-status-warning-50 border-status-warning-100 text-status-warning-700';
      case 'error':
        return 'bg-status-error-50 border-status-error-100 text-status-error-700';
      default:
        return 'bg-status-info-50 border-status-info-100 text-status-info-700';
    }
  };

  return (
    <div className={`p-4 rounded-lg border ${getVariantClasses()} mb-6`}>
      <div className="flex items-center justify-between">
        <p className="text-body-md">{message}</p>
        {onClose && (
          <button
            onClick={onClose}
            className="ml-4 text-current hover:opacity-70 transition-opacity"
          >
            ×
          </button>
        )}
      </div>
    </div>
  );
};
